import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useMutation } from "@apollo/client";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  Stack,
  Switch,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { ADMIN_PROVISION_ORGANIZATION } from "./graphql";

type Schedule = "IMMEDIATE" | "ON_NEXT_REQUEST_CLOSE" | "MANUAL";
type RecipientMode = "DEFAULT" | "APPEND" | "REPLACE";

const initialForm = {
  organizationName: "",
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  billingName: "",
  address: "",
  city: "",
  state: "",
  onboardingSchedule: "MANUAL" as Schedule,
  recipientMode: "DEFAULT" as RecipientMode,
  additionalEmails: "",
  enableNotificationsNow: false,
};

export const ProvisionOrganization = () => {
  const history = useHistory();
  const toast = useToast();
  const [form, setForm] = useState(initialForm);

  const set =
    (key: keyof typeof form) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((current) => ({
        ...current,
        [key]:
          event.target instanceof HTMLInputElement && event.target.type === "checkbox"
            ? event.target.checked
            : event.target.value,
      }));

  const [provision, { loading }] = useMutation(ADMIN_PROVISION_ORGANIZATION, {
    onCompleted: () => {
      toast({ title: "Organization created", status: "success" });
      history.push("/orders/create");
    },
    onError: (error) =>
      toast({
        title: "Could not create organization",
        description: error.message,
        status: "error",
      }),
  });

  const canEnableNow = form.onboardingSchedule === "IMMEDIATE";

  const submit = () => {
    provision({
      variables: {
        data: {
          organizationName: form.organizationName,
          primaryContact: {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            phoneNumber: form.phoneNumber,
          },
          billingName: form.billingName,
          billingAddress: { address: form.address, city: form.city, state: form.state },
          onboardingSchedule: form.onboardingSchedule,
          recipientMode: form.recipientMode,
          additionalEmails: form.additionalEmails
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean),
          enableNotificationsNow: canEnableNow ? form.enableNotificationsNow : false,
        },
      },
    });
  };

  return (
    <Box maxW="640px" mx="auto" py={8}>
      <Heading size="md" mb={6}>
        Create Organization
      </Heading>
      <Stack spacing={4}>
        <FormControl isRequired>
          <FormLabel>Organization name</FormLabel>
          <Input value={form.organizationName} onChange={set("organizationName")} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Contact first name</FormLabel>
          <Input value={form.firstName} onChange={set("firstName")} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Contact last name</FormLabel>
          <Input value={form.lastName} onChange={set("lastName")} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Contact email</FormLabel>
          <Input type="email" value={form.email} onChange={set("email")} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Contact phone</FormLabel>
          <Input value={form.phoneNumber} onChange={set("phoneNumber")} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Billing name</FormLabel>
          <Input value={form.billingName} onChange={set("billingName")} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Billing address</FormLabel>
          <Input value={form.address} onChange={set("address")} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>City</FormLabel>
          <Input value={form.city} onChange={set("city")} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>State</FormLabel>
          <Input value={form.state} onChange={set("state")} />
        </FormControl>

        <FormControl>
          <FormLabel>Onboarding email</FormLabel>
          <Select value={form.onboardingSchedule} onChange={set("onboardingSchedule")}>
            <option value="MANUAL">Send manually later</option>
            <option value="ON_NEXT_REQUEST_CLOSE">Send when the next request closes</option>
            <option value="IMMEDIATE">Send immediately</option>
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel>Notification recipients</FormLabel>
          <Select value={form.recipientMode} onChange={set("recipientMode")}>
            <option value="DEFAULT">Default (contact + facility)</option>
            <option value="APPEND">Default + group inbox</option>
            <option value="REPLACE">Group inbox only</option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Additional notification emails (comma-separated)</FormLabel>
          <Textarea
            value={form.additionalEmails}
            onChange={set("additionalEmails")}
            placeholder="ops@acme.test, facilities@acme.test"
          />
        </FormControl>

        <FormControl display="flex" alignItems="center">
          <FormLabel mb={0}>
            Enable notifications now{" "}
            {canEnableNow ? "" : "(only when sending onboarding immediately)"}
          </FormLabel>
          <Switch
            isChecked={form.enableNotificationsNow}
            onChange={set("enableNotificationsNow")}
            isDisabled={!canEnableNow}
          />
        </FormControl>

        <Button colorScheme="blue" onClick={submit} isLoading={loading}>
          Create organization
        </Button>
      </Stack>
    </Box>
  );
};
